import * as React from "react"

import { flexRender, type Column, type Row } from "@tanstack/react-table"

import { cn } from "../../cn"
import { Skeleton } from "../Skeleton/Skeleton"
import {
  ActionCell,
  ExpandToggle,
  RowContextMenu,
  SelectCell,
} from "./TableChrome"
import {
  alignClasses,
  columnAlign,
  EDGE_GUTTER_CLASS,
  EDGE_TO_EDGE_ROW_CLASS,
  edgeFadeClasses,
  ESTIMATED_ROW_HEIGHT,
  gutterCoverClasses,
  NEST_INDENT,
  pinnedCellClasses,
  pinnedCellStyle,
  resolveRowCopyTargets,
  rowHighlightClass,
  shouldNavigateRow,
  SKELETON_ROW_COUNT,
  STICKY_CELL_DIVIDER,
  variantClasses,
  type PinMeta,
} from "./TableInternals"
import type { ColumnDef, SelectionModifiers, TableProps } from "./types"

// In `cardRows` mode each grouped section is separated from the previous one by
// this leading gap on every group row past the first, mirroring the card view's
// `gap-10` (2.5rem ≈ 36px) between sections. Applied as `paddingTop` (border-box)
// so the centered title drops into the band below the gap; the first section
// hugs the top edge like the card view. The consumer's `estimateRowHeight` must
// add the same value to those group rows so the virtualizer reserves the room
// (see MetricsTableView's METRIC_SECTION_GAP).
const CARD_ROW_SECTION_GAP = 36
// In `cardRows` mode an expanded group's title is separated from its platter by
// this trailing gap, mirroring the card view's `mb-4` (1rem ≈ 14px) below an
// expanded group header. Applied as `paddingBottom` (border-box) so it sits
// outside the platter, and only when the group is expanded (a collapsed section
// is separated from the next by CARD_ROW_SECTION_GAP alone). The consumer's
// `estimateRowHeight` must add the same value to expanded group rows.
const CARD_ROW_HEADER_GAP = 14

export function BodyRow<T>({
  row,
  columnsById,
  displayColumns,
  pinnedById,
  gridTemplateColumns,
  rowWidthClass,
  onRowClick,
  isRowClickable,
  rowHover,
  active,
  onPointerEnter,
  onPointerLeave,
  selectable,
  rowSelectable,
  rowSelectionDisabled,
  selectionAlwaysVisible,
  rowClickSelects,
  selectSticky,
  selectDivider,
  scrolledX,
  canScrollRight,
  actionsSticky,
  actionsEdgeFade,
  selected,
  variant,
  cellVariant,
  onToggleSelect,
  rowActions,
  rowCopyId,
  rowHref,
  hasActions,
  nestable,
  firstColumnId,
  renderGroupRow,
  start,
  size,
  index,
  measured,
  measureElement,
  rowAttributes,
  unreadIndicator,
  unread,
  edgeGutterPx,
  edgeToEdge,
  cardRows,
  platterLast,
}: {
  row: Row<T>
  columnsById: Map<string, ColumnDef<T>>
  displayColumns: Column<T>[]
  pinnedById: Map<string, PinMeta>
  gridTemplateColumns: string
  rowWidthClass: string
  onRowClick: TableProps<T>["onRowClick"]
  isRowClickable?: TableProps<T>["isRowClickable"]
  rowHover: boolean
  active: boolean
  onPointerEnter: TableProps<T>["onRowPointerEnter"]
  onPointerLeave: TableProps<T>["onRowPointerLeave"]
  selectable: boolean | undefined
  rowSelectable: boolean
  // This row's checkbox renders, but disabled — selectable in principle, blocked
  // right now (see `isRowSelectionDisabled`).
  rowSelectionDisabled: boolean
  // Keep this row's checkbox visible at rest instead of revealing it on hover.
  selectionAlwaysVisible: boolean
  // This table's `onRowClick` selects rather than navigates, so the select cell
  // keeps taking row clicks instead of opting out of them.
  rowClickSelects: boolean
  selectSticky: boolean
  selectDivider: boolean
  scrolledX: boolean
  canScrollRight: boolean
  actionsSticky: boolean
  actionsEdgeFade: boolean
  selected: boolean
  variant: "warning" | undefined
  cellVariant: TableProps<T>["cellVariant"]
  onToggleSelect: (id: string, modifiers: SelectionModifiers) => void
  rowActions: TableProps<T>["rowActions"]
  rowCopyId: TableProps<T>["rowCopyId"]
  rowHref: TableProps<T>["rowHref"]
  // Whether the grid reserves the trailing actions track, which the copy group
  // can turn on by itself.
  hasActions: boolean
  nestable: boolean
  firstColumnId: string | undefined
  renderGroupRow: TableProps<T>["renderGroupRow"]
  start: number
  size: number
  index: number
  // When `measured`, the row's real height is read from the DOM instead of the
  // virtualizer's estimate: it attaches `measureElement` and omits the fixed
  // height so it can grow to fit its content (e.g. an expanded cell).
  measured: boolean
  measureElement: (node: Element | null) => void
  // Consumer-supplied DOM attributes for this row (e.g. `data-testid`).
  rowAttributes:
    | Record<string, string | number | boolean | undefined>
    | undefined
  // Whether the unread-indicator feature is enabled (any row reserves the 1px
  // leading border so read/unread rows stay geometrically identical).
  unreadIndicator: boolean
  // Whether this specific row is unread (colors the reserved leading border).
  unread: boolean
  // Resolved outer-gutter width (0 when `edgeToEdge`) for sticky-column offsets.
  edgeGutterPx: number
  // Drop the outer cell's leading / trailing padding so columns sit flush.
  edgeToEdge: boolean
  // Render this row inside a platter (the platter itself paints the frame and
  // the surface; the row only carries its divider and highlight).
  cardRows: boolean
  // Whether this is the bottom row of its platter, which drops its divider.
  platterLast: boolean
}) {
  // Group / section rows short-circuit the column grid: they render as a single
  // full-width band with the expand/collapse chevron + the consumer's label.
  const groupContent = renderGroupRow?.(row.original)
  if (groupContent != null) {
    const canExpand = row.getCanExpand()
    const toggle = row.getToggleExpandedHandler()
    return (
      <div
        {...rowAttributes}
        role="row"
        data-row-id={row.id}
        data-unread={unreadIndicator && unread ? "true" : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? row.getIsExpanded() : undefined}
        // Full-width band (`w-full` = viewport width, not the possibly-wider
        // grid) so the group chrome reads as a solid bar; the leading gutter
        // matches the data rows' edge.
        className={cn(
          "group absolute left-0 top-0 flex w-full items-center",
          // Card mode matches the card view's group header (`gap-2` between the
          // chevron, icon, and label) so the section title lands at the same x;
          // other tables keep the tighter `gap-1.5`.
          cardRows ? "gap-2" : "gap-1.5",
          // Card mode drops the section divider + surface so the title floats on
          // the page background like the card view; other tables keep the band.
          !cardRows && "border-b border-coval-border bg-surface-contrast",
          EDGE_GUTTER_CLASS,
          edgeToEdge && !cardRows && EDGE_TO_EDGE_ROW_CLASS,
          // Reserve the same leading unread border as data rows so grouped
          // tables stay geometrically uniform and an unread group row shows the
          // accent instead of silently rendering as read.
          unreadIndicator && "border-l border-l-transparent",
          unreadIndicator && unread && "border-l-coval-text",
          canExpand &&
            "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
        )}
        style={{
          height: size,
          transform: `translateY(${start}px)`,
          // Gap between sections, like the card view's `gap-10`: pad above every
          // group row past the first so its title sits a section-gap below the
          // preceding platter. The first section hugs the top edge.
          ...(cardRows && index > 0
            ? { paddingTop: CARD_ROW_SECTION_GAP }
            : {}),
          // Gap below an expanded title, like the card view's `mb-4`: pad below
          // so the platter starts a header-gap under the title (collapsed groups
          // add nothing — the section gap alone separates them).
          ...(cardRows && row.getIsExpanded()
            ? { paddingBottom: CARD_ROW_HEADER_GAP }
            : {}),
        }}
        onMouseEnter={onPointerEnter ? () => onPointerEnter(row.id) : undefined}
        onMouseLeave={onPointerLeave ? () => onPointerLeave(row.id) : undefined}
        onClick={
          canExpand
            ? (event) => {
                // The chevron button toggles on its own; only the rest of the
                // band toggles here (mirrors data-row navigation gating).
                if (shouldNavigateRow(event.target)) {
                  toggle()
                }
              }
            : undefined
        }
        onKeyDown={
          canExpand
            ? (event) => {
                // Gate on `shouldNavigateRow` (like `onClick` above) so a
                // Space/Enter on the chevron button doesn't fire the button's
                // click *and* this handler — a double-toggle that cancels out.
                if (
                  (event.key === "Enter" || event.key === " ") &&
                  shouldNavigateRow(event.target)
                ) {
                  event.preventDefault()
                  toggle()
                }
              }
            : undefined
        }
      >
        {canExpand ? (
          <ExpandToggle
            expanded={row.getIsExpanded()}
            onToggle={toggle}
            // Platter group chevrons read as a lighter affordance than the title.
            className={cardRows ? "opacity-50" : undefined}
          />
        ) : (
          <span aria-hidden className="size-4 shrink-0" />
        )}
        <div
          className={cn(
            "flex min-w-0 grow items-center",
            // Match the card view's icon-to-label spacing in card mode (see the
            // group-row gap above); other tables keep the tighter `gap-1.5`.
            cardRows ? "gap-2" : "gap-1.5",
          )}
        >
          {groupContent}
        </div>
      </div>
    )
  }

  // A row click in a picker *is* the selection, so a row that can't be selected
  // — never, or not right now — has nothing for a click to do. It reads as
  // blocked rather than offering the pointer and focus ring of a live target.
  const selectionBlocked =
    Boolean(selectable) &&
    rowClickSelects &&
    (!rowSelectable || rowSelectionDisabled)
  const clickable =
    Boolean(onRowClick) &&
    (!isRowClickable || isRowClickable(row.original)) &&
    !selectionBlocked
  // Either state paints the row and its opaque cells (see `rowHighlightClass`),
  // and either one suppresses the hover tint; `selected` alone still governs
  // checkbox state, and only `selected` takes the blue wash.
  const highlighted = selected || active
  // Whether the row tints under the pointer at all. The opaque cells paint
  // their own background, so they take this rather than `clickable` — the row
  // keeps its cursor and focus ring either way.
  const hoverable = clickable && rowHover && !highlighted
  const warning = variant === "warning"
  const cellById = new Map(
    row.getVisibleCells().map((cell) => [cell.column.id, cell]),
  )
  const actions = rowActions ? rowActions(row.original) : []
  const copy = resolveRowCopyTargets({
    row: row.original,
    rowId: row.id,
    rowCopyId,
    rowHref,
  })
  const dataRow = (
    <div
      {...rowAttributes}
      role="row"
      // `measureElement` reads `data-index` to report the row's measured size
      // back to the virtualizer; only attached for opt-in measured rows.
      ref={measured ? measureElement : undefined}
      data-index={index}
      data-row-id={row.id}
      data-unread={unreadIndicator && unread ? "true" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={cn(
        // Width per `rowWidthClass`: viewport-width so `fr` tracks reflow and
        // long cells truncate, widening to the full grid only when pinned
        // columns overflow the viewport (so the bottom border spans them).
        "group absolute left-0 top-0 grid items-center",
        // Platter mode: the platter paints the frame and the surface, so a row
        // only carries the divider to the next one — dropped on the bottom row,
        // where the platter's own border closes the stack off.
        cardRows
          ? !platterLast && "border-b border-coval-border"
          : "border-b border-coval-border",
        EDGE_GUTTER_CLASS,
        edgeToEdge && !cardRows && EDGE_TO_EDGE_ROW_CLASS,
        rowWidthClass,
        // Unread indicator: every row reserves a 1px leading border (transparent
        // when read) so toggling read/unread never shifts the row's content; an
        // unread row colors it high-contrast. Only reserved when the feature is
        // enabled so other tables keep their exact geometry.
        unreadIndicator && "border-l border-l-transparent",
        unreadIndicator && unread && "border-l-coval-text",
        clickable &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-text",
        // Hover tints one step toward the contrast surface in both modes. A
        // highlighted row is skipped rather than overridden: it already carries
        // the stronger surface, and a hover that lightened it would read as the
        // pointer undoing the selection.
        hoverable && "hover:bg-surface-contrast",
        rowHighlightClass({ selected, active, cardRows }),
        warning && "bg-coval-warning-surface text-coval-warning-on-surface",
        warning && hoverable && "hover:bg-coval-warning-surface",
      )}
      style={{
        gridTemplateColumns,
        // Measured rows size to their content (no fixed height) so they can
        // grow; the virtualizer reads the real height from the DOM.
        ...(measured ? {} : { height: size }),
        transform: `translateY(${start}px)`,
      }}
      onMouseEnter={onPointerEnter ? () => onPointerEnter(row.id) : undefined}
      onMouseLeave={onPointerLeave ? () => onPointerLeave(row.id) : undefined}
      onClick={
        clickable
          ? (event) => {
              if (!shouldNavigateRow(event.target)) {
                return
              }
              onRowClick?.(row.original, event)
            }
          : undefined
      }
      onKeyDown={
        clickable
          ? (event) => {
              if (
                (event.key === "Enter" || event.key === " ") &&
                shouldNavigateRow(event.target)
              ) {
                event.preventDefault()
                event.currentTarget.click()
              }
            }
          : undefined
      }
    >
      {selectable && (
        <SelectCell
          selectable={rowSelectable}
          disabled={rowSelectionDisabled}
          alwaysVisible={selectionAlwaysVisible}
          rowClickSelects={rowClickSelects}
          selected={selected}
          active={active}
          warning={warning}
          onToggle={(modifiers) => onToggleSelect(row.id, modifiers)}
          sticky={selectSticky}
          divider={selectDivider}
          hoverable={hoverable}
          edgeGutterPx={edgeGutterPx}
          cardRows={cardRows}
        />
      )}
      {displayColumns.map((leaf) => {
        const cell = cellById.get(leaf.id)
        if (!cell) {
          return null
        }
        const col = columnsById.get(leaf.id)
        const pin = pinnedById.get(leaf.id)
        const cellWarning = cellVariant?.(row.original, leaf.id) === "warning"
        const align = columnAlign(col)
        /*
         * `UiColumnDef.cell` is a render function, not a component, so call it
         * directly. Routing it through `flexRender` would mount it as a
         * component type, and the adapter builds a fresh closure per column
         * rebuild — so every rebuild would swap the type and remount the whole
         * cell subtree, discarding any state a cell holds.
         */
        const content = col?.cell
          ? col.cell(row.original)
          : flexRender(cell.column.columnDef.cell, cell.getContext())
        const body =
          nestable && leaf.id === firstColumnId && !cardRows ? (
            // Expand/collapse control + depth indent live in the first column.
            // A spacer keeps non-expandable rows aligned with their siblings.
            <div
              className="flex min-w-0 grow items-center gap-1"
              style={{ paddingLeft: row.depth * NEST_INDENT }}
            >
              {row.getCanExpand() ? (
                <ExpandToggle
                  expanded={row.getIsExpanded()}
                  onToggle={row.getToggleExpandedHandler()}
                />
              ) : (
                <span aria-hidden className="size-4 shrink-0" />
              )}
              {content}
            </div>
          ) : (
            // Card rows indent via `cardNestInset`, so no in-cell spacer/indent.
            content
          )
        // A pinned cell paints outside its own box: the outermost one covers the
        // gutter beyond its edge (`gutterCoverClasses`, an `after:`) and an edge
        // cell fades content into its inner edge (`edgeFadeClasses`, a
        // `before:`). `overflow-hidden` on the cell would clip both, so a pinned
        // cell clips its content in an inner wrapper and stays
        // `overflow-visible`. Center cells clip on the cell directly.
        const clipsInWrapper = pin != null
        return (
          <div
            key={cell.id}
            role="cell"
            style={pinnedCellStyle(pin)}
            className={cn(
              // `min-w-0` overrides a grid item's automatic minimum size, which
              // is what otherwise lets a cell whose content can't shrink (an
              // audio waveform, a long unbreakable value) grow past its own
              // track and spill across the next column.
              //
              // `isolate` makes the cell its own stacking context, so a
              // z-index set by cell content resolves inside the cell instead of
              // against the row. Without it, content that stacks itself above
              // `z-index: 1` paints over a pinned column, which is sticky at
              // exactly that level — an audio waveform renders in a shadow tree
              // whose wrapper is `z-index: 2` and cursor `z-index: 5`, and its
              // host establishes no stacking context of its own, so the
              // waveform drew across the pinned ID column once the table
              // overflowed and slid that column over the waveform's track.
              "isolate flex min-w-0 items-center px-3 py-2",
              clipsInWrapper ? "overflow-visible" : "overflow-hidden",
              variantClasses(col?.variant),
              alignClasses(align),
              cellWarning &&
                "bg-coval-warning-surface text-coval-warning-on-surface",
              cellWarning &&
                hoverable &&
                "group-hover:bg-coval-warning-surface",
              pin &&
                pinnedCellClasses(pin, {
                  selected,
                  active,
                  warning: warning || cellWarning,
                  hoverable,
                  scrolledX,
                  canScrollRight,
                  cardRows,
                }),
            )}
          >
            {clipsInWrapper ? (
              <div
                className={cn(
                  "flex w-full min-w-0 items-center overflow-hidden",
                  align === "right" && "justify-end",
                  align === "center" && "justify-center",
                )}
              >
                {body}
              </div>
            ) : (
              body
            )}
          </div>
        )
      })}
      {hasActions && (
        <ActionCell
          row={row.original}
          actions={actions}
          copy={copy}
          selected={selected}
          active={active}
          warning={warning}
          hoverable={hoverable}
          sticky={actionsSticky}
          edgeFade={actionsEdgeFade}
          edgeGutterPx={edgeGutterPx}
          cardRows={cardRows}
        />
      )}
    </div>
  )
  return (
    <RowContextMenu row={row.original} actions={actions} copy={copy}>
      {dataRow}
    </RowContextMenu>
  )
}

export function LoadingRows<T>({
  displayColumns,
  pinnedById,
  gridTemplateColumns,
  rowWidthClass,
  selectable,
  selectSticky,
  selectDivider,
  scrolledX,
  canScrollRight,
  hasActions,
  edgeGutterPx,
  edgeToEdge,
  cardRows,
}: {
  displayColumns: Column<T>[]
  pinnedById: Map<string, PinMeta>
  gridTemplateColumns: string
  rowWidthClass: string
  selectable: boolean | undefined
  selectSticky: boolean
  selectDivider: boolean
  scrolledX: boolean
  canScrollRight: boolean
  hasActions: boolean
  edgeGutterPx: number
  edgeToEdge: boolean
  cardRows: boolean
}) {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          role="row"
          aria-hidden="true"
          // Fixed row height matches the loaded rows (the virtualizer renders
          // them at `ESTIMATED_ROW_HEIGHT`), so the skeleton doesn't reflow to a
          // shorter row height when the data arrives.
          className={cn(
            "grid items-center",
            // Match the loaded rows: one platter, or divider rows.
            cardRows
              ? cn(
                  "border-x border-b border-coval-border bg-surface-default",
                  rowIndex === 0 && "rounded-t-md border-t",
                  rowIndex === SKELETON_ROW_COUNT - 1 && "rounded-b-md",
                )
              : "border-b border-coval-border",
            EDGE_GUTTER_CLASS,
            edgeToEdge && !cardRows && EDGE_TO_EDGE_ROW_CLASS,
            rowWidthClass,
          )}
          style={{ gridTemplateColumns, height: ESTIMATED_ROW_HEIGHT }}
        >
          {selectable && (
            <div
              style={
                selectSticky
                  ? { position: "sticky", left: edgeGutterPx, zIndex: 1 }
                  : undefined
              }
              className={cn(
                "flex items-center justify-center px-3 py-2",
                // Match the data rows: carry the row divider under the opaque
                // sticky cell (see STICKY_CELL_DIVIDER).
                selectSticky && cn(STICKY_CELL_DIVIDER, "bg-surface-default"),
                // Fade the data columns into the selection column as they scroll
                // under it.
                selectDivider && edgeFadeClasses("left"),
                selectSticky && gutterCoverClasses("left"),
              )}
            />
          )}
          {/* Mirror the data rows' pinned layout so pinned columns and the
              selection column stay put while the skeleton scrolls horizontally. */}
          {displayColumns.map((leaf) => {
            const pin = pinnedById.get(leaf.id)
            return (
              <div
                key={leaf.id}
                style={pinnedCellStyle(pin)}
                className={cn(
                  "flex min-w-0 items-center px-3 py-2",
                  pin && pinnedCellClasses(pin, { scrolledX, canScrollRight }),
                )}
              >
                <Skeleton variant="text" />
              </div>
            )
          })}
          {hasActions && <div className="px-3 py-2" />}
        </div>
      ))}
    </>
  )
}
